import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, apiDownload } from '../lib/api';

// ── Tipi ──────────────────────────────────────────────────────────────────
type EntitaTypePrint = 'commessa' | 'capitolato' | 'offerta_capitolato' | 'preventivo' | 'dati_societari' | 'movimenti_analitica' | 'bilancio_analitica' | 'ordine_go' | 'sal'
  | 'ddt' | 'fattura_cliente' | 'ordine_cliente' | 'distinta' | 'proforma_acconto' | 'ordine_fornitore' | 'carico_merce';

type VistaStampa = 'APPALTATORE' | 'ACQUIRENTE';

const MODULO_PER_ENTITA: Record<EntitaTypePrint, string> = {
  commessa:           'COMMESSA',
  capitolato:         'CAPITOLATO',
  offerta_capitolato: 'OFFERTA_CAPITOLATO',
  preventivo:         'PREVENTIVO',
  dati_societari:     'DATI_SOCIETARI',
  movimenti_analitica: 'MOVIMENTI_ANALITICA',
  bilancio_analitica:  'BILANCIO_ANALITICA',
  // entitaId e' il caricamento, non il singolo ordine: la stampa e' cumulativa.
  ordine_go:           'ORDINE_GO',
  sal:                 'SAL',
  ddt:                 'DDT',
  fattura_cliente:     'FATTURA',
  ordine_cliente:      'ORDINE_CLIENTE',
  distinta:            'DISTINTA',
  proforma_acconto:    'PROFORMA_ACCONTO',
  ordine_fornitore:    'ORDINE_FORNITORE',
  // solo movimenti con causale di tipo CARICO
  carico_merce:        'CARICO_MERCE',
};

type ExtraOptValue = string | number | boolean | number[];

interface PrintModalProps {
  societaId: number;
  entitaType: EntitaTypePrint;
  entitaId: number;
  entitaLabel: string;
  emailDestinatarioDefault?: string;
  /**
   * Solo per preventivi CONFIGURATO_COMMESSA con acquirente.
   * Determina quale JSON di stampa viene prodotto e a chi vanno suggeriti
   * i destinatari email. Se assente, comportamento legacy del backend.
   */
  vista?: VistaStampa;
  /** Se passati, vengono propagati al backend per controllare il PDF generato. */
  conTotali?: boolean;
  conAllegati?: boolean;
  /**
   * Opzioni aggiuntive specifiche del modulo (es. DATI_SOCIETARI: includi*, bilanciIds).
   * Vengono propagate sia al query string del runtime editor sia al body delle POST.
   */
  extraOpts?: Record<string, ExtraOptValue>;
  onClose: () => void;
}

interface TemplateOption {
  id: number;
  nome: string;
  descrizione: string | null;
}

type Azione = 'anteprima' | 'pdf' | 'email';

const AZIONI: { value: Azione; label: string; btnLabel: string }[] = [
  { value: 'anteprima', label: 'Anteprima HTML', btnLabel: 'Anteprima' },
  { value: 'pdf', label: 'Scarica PDF', btnLabel: 'Scarica PDF' },
  { value: 'email', label: 'Invia Email', btnLabel: 'Apri invio' },
];

export default function PrintModal({ societaId, entitaType, entitaId, entitaLabel, emailDestinatarioDefault, vista, conTotali, conAllegati, extraOpts, onClose }: PrintModalProps) {
  const conTotaliEff   = conTotali   !== false;
  const conAllegatiEff = conAllegati !== false;

  // Serializza extraOpts in URLSearchParams (array → CSV)
  const applyExtraOpts = (params: URLSearchParams) => {
    if (!extraOpts) return;
    Object.entries(extraOpts).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (Array.isArray(v)) {
        if (v.length > 0) params.set(k, v.join(','));
      } else {
        params.set(k, String(v));
      }
    });
  };
  const [templates, setTemplates] = useState<TemplateOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [azione, setAzione] = useState<Azione>('anteprima');
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Carica template disponibili
  const caricaTemplates = useCallback(async () => {
    setLoading(true);
    try {
      // @api GET /api/report-templates?societaId=&moduloGestionale={modulo}&attivo=true -> {"success":true,"data":[...]}
      const moduloGestionale = MODULO_PER_ENTITA[entitaType];
      const r = await apiFetch<{ success: boolean; data: TemplateOption[] }>(
        `/api/report-templates?societaId=${societaId}&moduloGestionale=${moduloGestionale}&attivo=true`
      );
      const list = r.data || [];
      setTemplates(list);
      if (list.length > 0) setTemplateId(list[0].id);
    } catch {
      setError('Errore nel caricamento dei template');
    } finally {
      setLoading(false);
    }
  }, [societaId, entitaType]);

  useEffect(() => { caricaTemplates(); }, [caricaTemplates]);

  const templateSelezionato = templates.find(t => t.id === templateId);

  const esegui = async () => {
    if (!templateId) return;
    setExecuting(true);
    setError('');
    setSuccessMsg('');

    try {
      if (azione === 'anteprima') {
        // Apre l'editor in modalità runtime — dati reali + toolbar PDF/Invia
        const params = new URLSearchParams({
          entitaType,
          entitaId: String(entitaId),
        });
        if (vista) params.set('vista', vista);
        if (!conTotaliEff)   params.set('conTotali', 'false');
        if (!conAllegatiEff) params.set('conAllegati', 'false');
        applyExtraOpts(params);
        window.open(`/print/${templateId}?${params.toString()}`, '_blank');
        onClose();
      } else if (azione === 'pdf') {
        // @api POST /api/print/pdf/:templateId {"entitaType":"string","entitaId":"number","vista":"string?","conTotali":"boolean?","conAllegati":"boolean?","...extraOpts":"any"} -> stream PDF
        const vistaSuffix = vista ? `-${vista.toLowerCase()}` : '';
        await apiDownload(
          `/api/print/pdf/${templateId}`,
          `${entitaType}-${entitaId}${vistaSuffix}.pdf`,
          {
            method: 'POST',
            body: JSON.stringify({
              entitaType, entitaId, vista,
              conTotali: conTotaliEff, conAllegati: conAllegatiEff,
              ...(extraOpts || {}),
            }),
          }
        );
      } else if (azione === 'email') {
        // Apre il runtime editor con il pannello Invia già aperto.
        // I destinatari e il mittente vengono gestiti lì.
        const params = new URLSearchParams({
          entitaType,
          entitaId: String(entitaId),
          autoOpenInvia: '1',
        });
        if (vista) params.set('vista', vista);
        if (!conTotaliEff)   params.set('conTotali', 'false');
        if (!conAllegatiEff) params.set('conAllegati', 'false');
        applyExtraOpts(params);
        window.open(`/print/${templateId}?${params.toString()}`, '_blank');
        onClose();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Errore durante l\'operazione');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="bop-modal-overlay">
      <div className="bop-modal bop-modal--md">
        <div className="bop-modal__header">
          <h3 className="bop-modal__title">
            Stampa / Invia — {entitaLabel}
            {vista && (
              <span className="bop-badge bop-badge-info" style={{ marginLeft: 'var(--bop-space-sm)' }}>
                Vista: {vista === 'APPALTATORE' ? 'Appaltatore' : 'Acquirente'}
              </span>
            )}
          </h3>
          <button className="bop-modal__close" onClick={onClose}>×</button>
        </div>
        <div className="bop-modal__body">
          {loading ? (
            <div className="bop-empty-state">Caricamento template...</div>
          ) : templates.length === 0 ? (
            <div className="bop-empty-state">
              Nessun template configurato per questo modulo. Vai su Impostazioni → Report per crearne uno.
            </div>
          ) : (
            <>
              {/* Step 1 — Selezione template */}
              <div className="bop-form-group">
                <label className="bop-label">Template</label>
                <select
                  className="bop-select"
                  value={templateId ?? ''}
                  onChange={e => setTemplateId(parseInt(e.target.value))}
                >
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                  ))}
                </select>
                {templateSelezionato?.descrizione && (
                  <div className="bop-text-muted">{templateSelezionato.descrizione}</div>
                )}
              </div>

              {/* Step 2 — Selezione azione */}
              <div className="bop-form-group">
                <label className="bop-label">Azione</label>
                <div className="bop-flex">
                  {AZIONI.map(a => (
                    <button
                      key={a.value}
                      className={`bop-btn ${azione === a.value ? 'bop-btn-primary' : 'bop-btn-secondary'}`}
                      onClick={() => setAzione(a.value)}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3 — Nota per email */}
              {azione === 'email' && (
                <div className="bop-text-muted" style={{ fontSize: 'var(--bop-font-sm)' }}>
                  Apre l'anteprima del documento con il pannello invio già attivo.
                  I destinatari vengono pre-compilati dall'anagrafica del cliente.
                </div>
              )}

              {error && <div className="bop-badge bop-badge-danger">{error}</div>}
              {successMsg && <div className="bop-badge bop-badge-success">{successMsg}</div>}
            </>
          )}
        </div>
        <div className="bop-modal__footer">
          <button className="bop-btn bop-btn-secondary" onClick={onClose}>Annulla</button>
          {templates.length > 0 && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={esegui}
              disabled={executing || !templateId}
            >
              {executing ? 'In corso…' : AZIONI.find(a => a.value === azione)?.btnLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
